import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Mic, MicOff, Phone, PhoneOff, Signal, Video, Volume1, Volume2 } from "lucide-react";
import type { CallInfo, CallQuality } from "../calls";
import type { NativeCallAudioRoute } from "../mobileBridge";
import type { User } from "../types";
import { UserAvatar } from "./UserAvatar";

type Props = Readonly<{
  call: CallInfo | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  cameraEnabled: boolean;
  muted: boolean;
  quality: CallQuality;
  peerName: string;
  peerProfile?: Pick<User, "avatarColor" | "avatarUrl" | "displayName"> | undefined;
  ownProfile?: Pick<User, "avatarColor" | "avatarUrl" | "displayName"> | undefined;
  nativeAudioRoute?: NativeCallAudioRoute | undefined;
  onToggleAudioRoute?: (() => void) | undefined;
  onAccept: () => void;
  onEnd: (state?: "ended" | "declined") => void;
  onToggleCamera: () => void;
  onToggleMute: () => void;
}>;

export function formatCallDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours > 0
    ? [hours, minutes, rest].map((value) => String(value).padStart(2, "0")).join(":")
    : [minutes, rest].map((value) => String(value).padStart(2, "0")).join(":");
}

export function CallOverlay({ call, cameraEnabled, localStream, muted, quality, remoteStream, peerName, peerProfile, ownProfile, nativeAudioRoute, onAccept, onEnd, onToggleAudioRoute, onToggleCamera, onToggleMute }: Props) {
  const localVideo = useRef<HTMLVideoElement>(null);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  const remoteAudio = useRef<HTMLAudioElement>(null);
  const [duration, setDuration] = useState(0);
  const [localPlaybackBlocked, setLocalPlaybackBlocked] = useState(false);
  const [remotePlaybackBlocked, setRemotePlaybackBlocked] = useState(false);

  useEffect(() => {
    const element = localVideo.current;
    if (!element) return;
    element.defaultMuted = true;
    element.muted = true;
    element.srcObject = localStream;
    let disposed = false;
    const play = () => {
      if (!localStream || localStream.getVideoTracks().length === 0) {
        setLocalPlaybackBlocked(false);
        return;
      }
      void element.play()
        .then(() => { if (!disposed) setLocalPlaybackBlocked(false); })
        .catch(() => { if (!disposed) setLocalPlaybackBlocked(true); });
    };
    play();
    element.addEventListener("loadedmetadata", play);
    element.addEventListener("canplay", play);
    return () => {
      disposed = true;
      element.removeEventListener("loadedmetadata", play);
      element.removeEventListener("canplay", play);
      if (element.srcObject === localStream) element.srcObject = null;
    };
  }, [call?.callId, localStream]);

  useEffect(() => {
    const element = call?.video ? remoteVideo.current : remoteAudio.current;
    if (!element) return;
    element.muted = false;
    element.volume = 1;
    element.srcObject = remoteStream;
    let disposed = false;
    const play = () => {
      if (!remoteStream || remoteStream.getTracks().length === 0) {
        setRemotePlaybackBlocked(false);
        return;
      }
      void element.play()
        .then(() => { if (!disposed) setRemotePlaybackBlocked(false); })
        .catch(() => { if (!disposed) setRemotePlaybackBlocked(true); });
    };
    play();
    element.addEventListener("loadedmetadata", play);
    element.addEventListener("canplay", play);
    return () => {
      disposed = true;
      element.removeEventListener("loadedmetadata", play);
      element.removeEventListener("canplay", play);
      if (element.srcObject === remoteStream) element.srcObject = null;
    };
  }, [call?.callId, call?.video, remoteStream]);

  const resumePlayback = async () => {
    const local = localVideo.current;
    const remote = call?.video ? remoteVideo.current : remoteAudio.current;
    const [localResult, remoteResult] = await Promise.allSettled([
      local?.srcObject ? local.play() : Promise.resolve(),
      remote?.srcObject ? remote.play() : Promise.resolve(),
    ]);
    setLocalPlaybackBlocked(localResult.status === "rejected");
    setRemotePlaybackBlocked(remoteResult.status === "rejected");
  };
  useEffect(() => {
    setDuration(0);
    if (call?.status !== "active") return;
    const timer = window.setInterval(() => setDuration((current) => current + 1), 1_000);
    return () => window.clearInterval(timer);
  }, [call?.callId, call?.status]);
  if (!call) return null;
  const incoming = call.direction === "incoming" && call.status === "ringing";
  const remoteVideoAvailable = Boolean(remoteStream?.getVideoTracks().some((track) => track.readyState === "live"));
  const localVideoAvailable = Boolean(cameraEnabled && localStream?.getVideoTracks().some((track) => track.enabled && track.readyState === "live"));
  const shownPeer = peerProfile ?? { avatarColor: "#617dff", avatarUrl: null, displayName: peerName };
  const videoProfile = quality.videoProfile === "fullhd"
    ? "Full HD"
    : quality.videoProfile === "hd" ? "HD" : quality.videoProfile === "balanced" ? "средний режим" : "экономия трафика";
  const mediaDetails = call.video
    ? [quality.resolution, quality.frameRate !== null ? `${quality.frameRate} FPS` : null, quality.codec, quality.videoProfile ? videoProfile : null].filter(Boolean).join(" · ")
    : [quality.codec ?? "Opus", quality.bitrateKbps !== null ? `${quality.bitrateKbps} Кбит/с` : null].filter(Boolean).join(" · ");
  return (
    <div className="call-overlay">
      <div className={`call-stage ${call.video ? "video-call" : "audio-call"}`}>
        {call.video && <video autoPlay className="remote-video" playsInline ref={remoteVideo} />}
        {call.video && <video autoPlay className="local-video" muted playsInline ref={localVideo} />}
        {call.video && !remoteVideoAvailable && <UserAvatar className="call-video-fallback-avatar remote" user={shownPeer} />}
        {call.video && !localVideoAvailable && ownProfile && <UserAvatar className="call-video-fallback-avatar local" user={ownProfile} />}
        {!call.video && <audio autoPlay ref={remoteAudio} />}
        <div className="call-copy">
          <UserAvatar className={`call-avatar ${call.video ? "compact" : ""}`} user={shownPeer} />
          <h2>{peerName}</h2>
          <p>{incoming ? (call.video ? "Входящий видеозвонок" : "Входящий аудиозвонок") : call.status === "active" ? `Защищённое соединение · ${formatCallDuration(duration)}` : "Устанавливаем соединение…"}</p>
          <small>{call.video ? <Video /> : <Mic />} Медиа шифруется WebRTC</small>
        </div>
        {!call.video && ownProfile && <div className="call-own-profile"><UserAvatar className="call-own-avatar" user={ownProfile} /><span><b>Вы</b><small>{ownProfile.displayName}</small></span></div>}
        {call.status === "active" && (
          <div className={`call-quality ${quality.level}`} title="Качество рассчитывается по задержке, потерям пакетов и скорости входящего потока">
            <Signal />
            <span>{quality.level === "good" ? "Хорошее" : quality.level === "fair" ? "Среднее" : quality.level === "poor" ? "Плохое" : "Проверяем"}</span>
            <small>{quality.latencyMs !== null ? `${quality.latencyMs} мс` : "—"} · {quality.packetLossPercent !== null ? `${quality.packetLossPercent}% потерь` : "без данных"} · {quality.route === "turn" ? "TURN" : quality.route === "direct" ? "напрямую" : "маршрут…"}</small>
            {mediaDetails && <small className="call-media-details">{mediaDetails}</small>}
          </div>
        )}
        {!incoming && (localPlaybackBlocked || remotePlaybackBlocked) && (
          <button className="call-resume-media" onClick={() => void resumePlayback()} type="button">
            <Volume2 /> Включить звук и видео
          </button>
        )}
        <div className="call-actions">
          {incoming && <button aria-label="Принять звонок" className="call-button accept" onClick={onAccept} type="button"><Phone /></button>}
          {!incoming && <button aria-label={muted ? "Включить микрофон" : "Выключить микрофон"} className={`call-button control ${muted ? "disabled" : ""}`} onClick={onToggleMute} type="button">{muted ? <MicOff /> : <Mic />}</button>}
          {!incoming && call.video && <button aria-label={cameraEnabled ? "Выключить камеру" : "Включить камеру"} className={`call-button control ${cameraEnabled ? "" : "disabled"}`} onClick={onToggleCamera} type="button">{cameraEnabled ? <Camera /> : <CameraOff />}</button>}
          {!incoming && onToggleAudioRoute && <button aria-label={nativeAudioRoute === "speaker" ? "Переключить на разговорный динамик" : "Включить громкую связь"} className={`call-button control ${nativeAudioRoute === "speaker" ? "active" : ""}`} onClick={onToggleAudioRoute} title={nativeAudioRoute === "speaker" ? "Разговорный динамик" : "Громкая связь"} type="button">{nativeAudioRoute === "speaker" ? <Volume2 /> : <Volume1 />}</button>}
          <button aria-label="Завершить звонок" className="call-button decline" onClick={() => onEnd(incoming ? "declined" : "ended")} type="button"><PhoneOff /></button>
        </div>
      </div>
    </div>
  );
}
